import { styled } from "@mui/material/styles";
import CorrespondenceDrawer from "messaging/correspondence/_views/correspondence-drawer";
import type { ForumRequest } from "messaging/conversations/conversation-links";
import ChatWindow from "messaging/forums/_views/components/forum-chat-window";
import Forum from "messaging/forums/forum";
import { describeWorkAgreementConversation } from "work/work-agreements/correspondence/work-agreement-conversation-titles";
import useSessionUser from "permissions/session/use-session-user";
import WorkAgreementDocumentsPanel from "work/work-agreements/documents/_views/work-agreement-documents-panel";
import useCopyAttachmentToWorkDocuments, {
  useAddSentAttachmentsToWorkDocuments,
} from "work/work-agreements/documents/_views/use-copy-attachment-to-work-documents";

const DrawerChatWindow = styled(ChatWindow)(() => ({
  flex: 1,
  height: "100%",
  maxHeight: "none",
  minHeight: 0,
}));

type CommentsDrawerProps = {
  /**
   * The agreement's conversation, whose documents the Documents tab lists: the first agreement in its
   * revision chain. Until it is known, the tab is not offered.
   */
  conversationId?: string;
  /** The work's name, which a request for upload access names to the people who may grant it. */
  workName?: string;
  forums: Forum[];
  readOnly?: boolean;
  isOpen: boolean;
  isOverlay?: boolean;
  /** The conversation the page's URL asked the pane to open on. */
  forumRequest?: ForumRequest;
  onClose: () => void;
};

/**
 * The work agreement's Correspondence pane: its forums' chat and the work's own documents, in the drawer
 * the proposal's pane opens in too.
 */
export default function CommentsDrawer(props: Readonly<CommentsDrawerProps>) {
  const { conversationId, workName, forums, readOnly, isOpen, isOverlay, forumRequest, onClose } = props;
  const copyAttachment = useCopyAttachmentToWorkDocuments(conversationId);
  const addSentAttachments = useAddSentAttachmentsToWorkDocuments(conversationId);
  const user = useSessionUser();
  // Copying a chat file into the work's documents creates a document, which asserts what an upload
  // does (`Document.CreateAsync`), so a reader who may not upload is not offered it (#6595).
  const mayAddDocuments = !readOnly && user?.canUploadDocuments() === true;

  return (
    <CorrespondenceDrawer isOpen={isOpen} isOverlay={isOverlay} onClose={onClose}>
      {/* Bottom-up, like every chat and correspondence pane: oldest message at the top, newest
          at the bottom, editor directly below (#5741). */}
      {/* No member list: the agreement's teams decide who is in each forum, and the tab names it (#5823). */}
      {/* Documents in place of Shared files: the work's own documents, not the chats' attachments,
          which stay in their messages (#6054). */}
      <DrawerChatWindow
        forums={forums}
        readOnly={readOnly}
        isOpen={isOpen}
        forumRequest={forumRequest}
        hideMembers
        describeConversation={describeWorkAgreementConversation}
        documents={
          conversationId ? <WorkAgreementDocumentsPanel conversationId={conversationId} workName={workName} readOnly={readOnly} /> : undefined
        }
        onCopyAttachment={mayAddDocuments ? copyAttachment : undefined}
        attachmentOption={mayAddDocuments ? addSentAttachments : undefined}
      />
    </CorrespondenceDrawer>
  );
}
